import { type DocumentFile, getExpirationStatus } from "../models/document";
// @ts-expect-error pure js
import { FileStatus } from "../models/documents";
// @ts-expect-error pure js
import { DIAGNOSTIC_TAG_ALIASES } from "./constants/documents";
// @ts-expect-error pure js
import { META_DIAGNOSTICS } from "./constants/documents";
import type { DiagnosticRequirement } from "./diagnosticRules";

export type DocumentStatusCode = "absent" | "expire" | "aValider" | "valide";

/**
 * Statut d'un type de document pour un locataire à partir de tous ses fichiers
 * portant ce tag. Les fichiers expirés ne comptent que s'il n'en reste aucun
 * en cours de validité ; parmi ceux-ci, un fichier en attente de validation
 * prime sur un fichier déjà validé, car il y a une action à faire.
 */
export function getDocumentStatus(
  files: DocumentFile[] | undefined,
  tag: string,
): DocumentStatusCode {
  const filesWithTag = (files ?? []).filter((file) => file.tag === tag);
  if (filesWithTag.length === 0) {
    return "absent";
  }
  const currentFiles = filesWithTag.filter(
    (file) => getExpirationStatus(file.valid_until) !== "expired",
  );
  if (currentFiles.length === 0) {
    return "expire";
  }
  if (currentFiles.some((file) => file.status !== FileStatus.valide.status)) {
    return "aValider";
  }
  return "valide";
}

export type TenantDocumentStatusCode = DocumentStatusCode | "bientotExpire";

export interface TenantDocumentState {
  status: TenantDocumentStatusCode;
  /** Échéance du document en cours de validité le plus récent. */
  validUntil?: string;
}

/**
 * Statut d'un type de document pour un locataire, avec son échéance : un
 * document valide dont l'échéance approche (même seuil que les actions
 * prioritaires) passe en « expire bientôt ».
 */
export function getTenantDocumentState(
  files: DocumentFile[] | undefined,
  tag: string,
): TenantDocumentState {
  const status = getDocumentStatus(files, tag);
  const dates = (files ?? [])
    .filter(
      (file) =>
        file.tag === tag &&
        file.valid_until &&
        getExpirationStatus(file.valid_until) !== "expired",
    )
    .map((file) => file.valid_until as string);
  if (dates.length === 0) {
    return { status };
  }
  const validUntil = dates.reduce((latest, date) =>
    date > latest ? date : latest,
  );
  if (status === "valide" && getExpirationStatus(validUntil) === "expiring") {
    return { status: "bientotExpire", validUntil };
  }
  return { status, validUntil };
}

export type DiagnosticStatusCode =
  | "manquant"
  | "expire"
  | "bientotExpire"
  | "valide"
  | "nonConcerne";

export const DIAGNOSTIC_STATUS_OPTIONS: {
  value: DiagnosticStatusCode;
  label: string;
}[] = [
  { value: "manquant", label: "Manquant" },
  { value: "expire", label: "Expiré" },
  { value: "bientotExpire", label: "Expire bientôt" },
  { value: "valide", label: "Valide" },
  { value: "nonConcerne", label: "Non concerné" },
];

export interface DiagnosticStatus {
  status: DiagnosticStatusCode;
  validUntil?: string;
}

/** Tags équivalents à un diagnostic : le sien et ses anciens tags. */
function tagsCovering(tag: string): string[] {
  const aliases = (DIAGNOSTIC_TAG_ALIASES as Record<string, string[]>)[tag];
  return [tag, ...(aliases ?? [])];
}

/** Un fichier vaut pour un diagnostic s'il en porte le tag (ou un ancien tag) ou s'il le regroupe (PDF multi-diagnostics). */
export function fileCoversDiagnostic(
  file: Pick<DocumentFile, "tag" | "meta">,
  tag: string,
): boolean {
  const tags = tagsCovering(tag);
  if (tags.includes(file.tag)) {
    return true;
  }
  const grouped = file.meta?.[META_DIAGNOSTICS];
  return (
    Array.isArray(grouped) && grouped.some((grouped) => tags.includes(grouped))
  );
}

/**
 * Statut d'un diagnostic du bien d'après le document le plus récent qui le couvre.
 * Un document sans date de validité (mesurage, termites, amiante négatif…) vaut
 * pour une durée illimitée.
 */
export function getDiagnosticStatus(
  files: DocumentFile[] | undefined,
  tag: string,
): DiagnosticStatus {
  const covering = (files ?? []).filter((file) =>
    fileCoversDiagnostic(file, tag),
  );
  if (covering.length === 0) {
    return { status: "manquant" };
  }
  if (covering.some((file) => !file.valid_until)) {
    return { status: "valide" };
  }
  const validUntil = covering
    .map((file) => file.valid_until as string)
    .reduce((latest, date) => (date > latest ? date : latest));
  const expiration = getExpirationStatus(validUntil);
  if (expiration === "expired") {
    return { status: "expire", validUntil };
  }
  return {
    status: expiration === "expiring" ? "bientotExpire" : "valide",
    validUntil,
  };
}

export interface DiagnosticSource {
  /** Nom du dossier (immeuble) qui porte les documents. */
  name: string;
  files: DocumentFile[] | undefined;
}

export interface PropertyDiagnostic extends DiagnosticStatus {
  /** Nom de l'immeuble dont le diagnostic est hérité, quand le bien n'en a pas en propre. */
  inheritedFrom?: string;
  /**
   * Règle qui rend le diagnostic exigible (statut « manquant ») ou non requis
   * (statut « nonConcerne » déduit du bien) ; absente pour une dispense manuelle.
   */
  reason?: string;
}

/**
 * Diagnostic d'un bien dans le pilotage : son propre document d'abord, sinon la
 * dispense manuelle « non concerné », sinon le document du dossier le plus proche
 * (`ancestors` du parent direct vers la racine), sinon « non requis » si les
 * caractéristiques du bien l'en dispensent (`requirement`), sinon manquant.
 * Un diagnostic non requis n'est jamais signalé expiré : seul un document en
 * cours de validité est montré.
 */
export function resolvePropertyDiagnostic(
  tag: string,
  {
    files,
    notApplicable = [],
    ancestors = [],
    requirement,
  }: {
    files: DocumentFile[] | undefined;
    notApplicable?: string[];
    ancestors?: DiagnosticSource[];
    requirement?: DiagnosticRequirement;
  },
): PropertyDiagnostic {
  const notRequired = requirement !== undefined && !requirement.required;
  const own = getDiagnosticStatus(files, tag);
  if (own.status !== "manquant") {
    if (!notRequired || own.status === "valide") {
      return own;
    }
    return { status: "nonConcerne", reason: requirement.reason };
  }
  if (notApplicable.includes(tag)) {
    return { status: "nonConcerne" };
  }
  for (const ancestor of ancestors) {
    const inherited = getDiagnosticStatus(ancestor.files, tag);
    if (inherited.status !== "manquant") {
      return { ...inherited, inheritedFrom: ancestor.name };
    }
  }
  if (notRequired) {
    return { status: "nonConcerne", reason: requirement.reason };
  }
  return { status: "manquant", reason: requirement?.reason };
}
